/**
 * Nations League — tier configuration.
 *
 * Four separate competitions on the sports API side, presented as one hub:
 *   fb:c:553 — League A (top tier)
 *   fb:c:100 — League B
 *   fb:c:83  — League C
 *   fb:c:101 — League D
 *
 * The URL segment "nationsleague" resolves to this hub; individual competition
 * IDs redirect here with ?tier=a|b|c|d preselected.
 */

export type TierKey = "A" | "B" | "C" | "D";

export interface TierConfig {
    tier: TierKey;
    /** URL query value (?tier=a). */
    slug: "a" | "b" | "c" | "d";
    competitionId: string;
    /** Label key under `labels.nationsLeague.tiers.*` for the tab. */
    labelKey: "a" | "b" | "c" | "d";
}

export const NL_COMPETITION_IDS = ["fb:c:553", "fb:c:100", "fb:c:83", "fb:c:101"] as const;

export const NL_TIERS: TierConfig[] = [
    { tier: "A", slug: "a", competitionId: "fb:c:553", labelKey: "a" },
    { tier: "B", slug: "b", competitionId: "fb:c:100", labelKey: "b" },
    { tier: "C", slug: "c", competitionId: "fb:c:83", labelKey: "c" },
    { tier: "D", slug: "d", competitionId: "fb:c:101", labelKey: "d" },
];

export const TIER_BY_SLUG: Record<string, TierConfig> = Object.fromEntries(
    NL_TIERS.map((t) => [t.slug, t]),
);

const TIER_BY_COMPETITION_ID: Record<string, TierConfig> = Object.fromEntries(
    NL_TIERS.map((t) => [t.competitionId, t]),
);

/**
 * If the given competition ID belongs to a Nations League tier, return the
 * hub URL (path only, no locale prefix) with the tier preselected via query.
 * Otherwise return null.
 */
export function getNationsLeagueHubRedirect(competitionId: string): string | null {
    const tier = TIER_BY_COMPETITION_ID[competitionId];
    if (!tier) return null;
    return `/football/competitions/nationsleague?tier=${tier.slug}`;
}

/** Which qualification rule applies to a standings row — drives row color. */
export type StandingRule =
    | "promotion"
    | "promotionPlayOffs"
    | "relegationPlayOffs"
    | "relegation"
    | "none";
